import { Link, Skeleton } from "@heroui/react";

import { EventStatusChip } from "@/src/components/EventStatusChip";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { AdminEvent } from "@/src/pages/Events/events.type";
import { ROUTES } from "@/src/routes/paths";

type AccountEventListProps = {
  events: AdminEvent[];
  /**
   * Le total du serveur.
   *
   * ⚠️ **Il ne vaut pas `events.length`** : la route borne chaque liste à cent.
   * C'est lui qui permet de dire « et 12 autres » au lieu de laisser croire que
   * la liste est entière.
   */
  total: number;
  isLoading: boolean;
};

/** Ce que le serveur rend au maximum par liste. */
const SERVER_LIMIT = 100;

/**
 * Les événements d'un compte, en liste courte — nom, statut, date.
 *
 * Chaque ligne ouvre la fiche de l'événement : c'est le chemin qui manquait
 * entre un compte signalé et les parties qu'il organise.
 */
export function AccountEventList({
  events,
  total,
  isLoading,
}: AccountEventListProps) {
  const t = useTranslations("accounts");
  const { locale } = useLocale();

  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-2">
        {[0, 1, 2].map((row) => (
          <Skeleton className="h-6 w-full" key={row} />
        ))}
      </div>
    );
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-1">
        {events.map((event) => (
          <li
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
            key={event.id}
          >
            <Link className="text-sm" href={ROUTES.event(event.id)}>
              {event.name}
            </Link>

            <EventStatusChip status={event.status} />

            <span className="text-xs text-muted">
              {event.date === null
                ? t("event-no-date")
                : formatter.format(new Date(event.date))}
            </span>
          </li>
        ))}
      </ul>

      {total > SERVER_LIMIT ? (
        <p className="text-xs text-muted">
          {t("events-truncated", { count: total - events.length })}
        </p>
      ) : null}
    </div>
  );
}
